import { useEffect, useState } from "react";
import { CheckCircle, XCircle, EnvelopeSimple, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError, useAuth } from "../lib/api";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

export default function AccessRequestsPage() {
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [tab, setTab] = useState("received");

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/access-requests?kind=received"),
      api.get("/access-requests?kind=sent"),
    ]);
    setReceived(r.data); setSent(s.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/access-requests/${id}/decide`, { decision });
      toast.success(decision === "approved" ? "Aprovada" : "Negada");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const rows = tab === "received" ? received : sent;

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-10">
      <div className="mb-8 border-b border-border pb-6">
        <div className="label-eyebrow mb-2">colaboração · acesso a provas</div>
        <h1 className="font-display text-5xl font-black tracking-tighter leading-none">Solicitações.</h1>
        <p className="text-muted-foreground mt-3 text-sm max-w-[520px]">
          Outros professores solicitaram acesso às suas provas — ou você pediu acesso às deles.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          ["received", `Recebidas (${received.filter(r => r.status === "pending").length})`],
          ["sent", `Enviadas (${sent.length})`],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2 text-xs font-mono-data uppercase tracking-widest border transition-colors ${
              tab === id ? "border-foreground bg-foreground text-background" : "border-border"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="border border-border bg-card">
        {rows.length === 0 ? (
          <div className="p-16 text-center label-eyebrow">
            {tab === "received" ? "nenhuma solicitação recebida" : "você não enviou solicitações"}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {rows.map(r => (
              <div key={r.id} data-testid={`req-row-${r.id}`} className="p-6 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <EnvelopeSimple size={16} weight="duotone" className="text-primary" />
                    <span className="font-mono-data text-sm font-semibold">
                      {tab === "received" ? r.requester?.name : r.owner?.name}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono-data">
                      {tab === "received" ? r.requester?.email : r.owner?.email}
                    </span>
                    <span
                      data-testid={`req-status-${r.id}`}
                      className={`ml-auto text-xs font-mono-data uppercase tracking-widest px-2 py-1 border ${
                        r.status === "pending" ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                          : r.status === "approved" ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
                          : "border-destructive text-destructive"
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                  <div className="text-sm mb-2">
                    Correção: <strong className="font-mono-data">{r.correction?.filename}</strong>{" "}
                    {r.correction?.cpf && <span className="text-muted-foreground">· CPF {r.correction.cpf}</span>}
                  </div>
                  {r.message && <div className="text-xs text-muted-foreground italic">"{r.message}"</div>}
                  <div className="text-xs text-muted-foreground font-mono-data mt-2">
                    {new Date(r.created_at).toLocaleString("pt-BR")}
                  </div>
                </div>

                <div className="flex flex-col gap-2 flex-shrink-0">
                  {tab === "received" && r.status === "pending" && (
                    <>
                      <Button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "approved")}
                        className="rounded-none font-mono-data uppercase tracking-widest text-xs">
                        <CheckCircle size={14} weight="bold" className="mr-2" />Aprovar
                      </Button>
                      <Button data-testid={`deny-${r.id}`} onClick={() => decide(r.id, "denied")} variant="outline"
                        className="rounded-none border-destructive text-destructive font-mono-data uppercase tracking-widest text-xs">
                        <XCircle size={14} weight="bold" className="mr-2" />Negar
                      </Button>
                    </>
                  )}
                  {tab === "sent" && r.status === "approved" && (
                    <Link to={`/correction/${r.correction_id}`}
                      className="inline-flex items-center gap-1 text-xs font-mono-data uppercase tracking-widest text-primary hover:underline">
                      Ver <ArrowUpRight size={12} weight="bold" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
